"use client";

import { useState } from "react";
import { ProductGrid } from "@/components/product-grid";
import { Button } from "@myapp/ui/button";
import { Badge } from "@myapp/ui/badge";
import type { Product } from "@/types/product";

// 샘플 데이터
const sampleProducts: Product[] = [
  {
    id: "1",
    name: "강원도 고랭지 배추",
    price: 15000,
    imageUrl: "https://images.unsplash.com/photo-1594282486929-e012e8b8e0a3?w=400&h=300&fit=crop",
    description: "고랭지에서 재배한 신선한 배추입니다. 김치용으로 최고!",
    tags: ["제철", "유기농", "농가직송"],
    buyUrl: "https://smartstore.naver.com/example",
    region: "강릉시",
    farmName: "청정농장",
    isInSeason: true,
    rating: 4.8,
    reviewCount: 127
  },
  {
    id: "2", 
    name: "횡성한우 등심",
    price: 45000,
    imageUrl: "https://images.unsplash.com/photo-1546833999-b9f581a1996d?w=400&h=300&fit=crop",
    description: "횡성에서 직접 기른 한우 등심입니다.",
    tags: ["특가", "인기", "횡성한우"],
    buyUrl: "https://smartstore.naver.com/example2",
    region: "횡성군",
    farmName: "한우목장",
    isInSeason: false,
    rating: 4.9,
    reviewCount: 85
  },
  {
    id: "3",
    name: "정선 곤드레나물",
    price: 8000,
    imageUrl: "https://images.unsplash.com/photo-1576045057995-568f588f82fb?w=400&h=300&fit=crop",
    description: "정선 깊은 산에서 직접 채취한 곤드레나물",
    tags: ["제철", "신선", "산나물"],
    buyUrl: "https://smartstore.naver.com/example3",
    region: "정선군",
    farmName: "산나물농장",
    isInSeason: true,
    rating: 4.6,
    reviewCount: 43
  },
  {
    id: "4",
    name: "춘천 닭갈비 양념육",
    price: 12000,
    imageUrl: "https://images.unsplash.com/photo-1598103442097-8b74394b95c6?w=400&h=300&fit=crop",
    description: "춘천 전통 방식으로 양념한 닭갈비용 고기",
    tags: ["할인", "춘천명물"],
    buyUrl: "https://smartstore.naver.com/example4",
    region: "춘천시",
    farmName: "춘천닭갈비",
    isInSeason: false,
    rating: 4.7,
    reviewCount: 156
  },
  {
    id: "5",
    name: "강릉 감자",
    price: 6000,
    imageUrl: "https://images.unsplash.com/photo-1518977676601-b53f82aba655?w=400&h=300&fit=crop",
    description: "강릉 바다 바람을 맞고 자란 맛있는 감자",
    tags: ["제철", "신선"],
    buyUrl: "https://smartstore.naver.com/example5",
    region: "강릉시",
    farmName: "바다농장",
    isInSeason: true,
    rating: 4.5,
    reviewCount: 92
  },
  {
    id: "6",
    name: "평창 버섯 모듬",
    price: 18000,
    imageUrl: "https://images.unsplash.com/photo-1622105808865-5e1ed7a1ad5d?w=400&h=300&fit=crop",
    description: "평창의 청정한 환경에서 기른 버섯 모듬",
    tags: ["추천", "유기농", "버섯"],
    buyUrl: "https://smartstore.naver.com/example6",
    region: "평창군",
    farmName: "청정버섯농장",
    isInSeason: false,
    rating: 4.4,
    reviewCount: 68
  }
];

export default function TestProductsPage() {
  const [showFavorite, setShowFavorite] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState(sampleProducts);

  const handleFavoriteToggle = (productId: string) => {
    console.log("Favorite toggled for product:", productId);
  };

  const handleLoadingToggle = () => {
    setLoading(!loading);
  };

  const handleFilterInSeason = () => {
    setSelectedProducts(sampleProducts.filter(p => p.isInSeason));
  };

  const handleShowAll = () => {
    setSelectedProducts(sampleProducts);
  };

  const handleShowEmpty = () => {
    setSelectedProducts([]);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="text-center space-y-4">
          <h1 className="text-3xl font-bold text-foreground">
            특산물 카드/그리드 컴포넌트 테스트
          </h1>
          <p className="text-muted-foreground">
            반응형 그리드와 상호작용 기능을 테스트해보세요.
          </p>
        </div>

        {/* 컨트롤 패널 */}
        <div className="bg-card border rounded-lg p-6">
          <h2 className="text-lg font-semibold mb-4">컨트롤 패널</h2>
          <div className="flex flex-wrap gap-3">
            <Button
              variant={showFavorite ? "default" : "outline"}
              onClick={() => setShowFavorite(!showFavorite)}
            >
              즐겨찾기 {showFavorite ? "켜짐" : "꺼짐"}
            </Button>
            
            <Button
              variant={loading ? "default" : "outline"}
              onClick={handleLoadingToggle}
            >
              로딩 상태 {loading ? "ON" : "OFF"}
            </Button>
            
            <Button variant="outline" onClick={handleShowAll}>
              전체 상품 ({sampleProducts.length}개)
            </Button>
            
            <Button variant="outline" onClick={handleFilterInSeason}>
              제철 상품만 보기
            </Button>
            
            <Button variant="outline" onClick={handleShowEmpty}>
              빈 상태 테스트
            </Button>
          </div>
        </div>

        {/* 현재 필터 정보 */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">현재 표시:</span>
          <Badge variant="outline">
            {selectedProducts.length}개 상품
          </Badge>
          {selectedProducts.length > 0 && selectedProducts.every(p => p.isInSeason) && (
            <Badge variant="default">제철 상품</Badge>
          )}
        </div>

        {/* 상품 그리드 */}
        <ProductGrid
          items={selectedProducts}
          loading={loading}
          showFavorite={showFavorite}
          onFavoriteToggle={handleFavoriteToggle}
        />

        {/* 기능 설명 */}
        <div className="space-y-4">
          <h2 className="text-xl font-semibold">컴포넌트 기능</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <h3 className="font-semibold text-primary">ProductCard 기능</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• 상품 이미지, 이름, 가격, 설명 표시</li>
                <li>• 태그 배지 (최대 3개)</li>
                <li>• 즐겨찾기 토글 (선택사항)</li>
                <li>• 제철 표시 배지</li>
                <li>• 평점 및 리뷰 수</li>
                <li>• 외부 구매 링크 (새 창)</li>
                <li>• 상세 페이지 링크</li>
                <li>• 이미지 로딩/에러 처리</li>
              </ul>
            </div>
            
            <div className="space-y-2">
              <h3 className="font-semibold text-primary">ProductGrid 기능</h3>
              <ul className="space-y-1 text-sm text-muted-foreground">
                <li>• 반응형 그리드 (모바일 2열, 태블릿 3열, 데스크톱 4열)</li>
                <li>• 로딩 스켈레톤 UI</li>
                <li>• 빈 상태 처리</li>
                <li>• 즐겨찾기 상태 관리</li>
                <li>• 접근성 지원</li>
                <li>• 호버 애니메이션</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}